import { cn } from "@/lib/utils";

/**
 * Single source of truth for rendering P&L values across the dashboard,
 * history, and cycle-detail surfaces.
 *
 * <h2>Honesty contract</h2>
 * The bot keeps three failure shapes for a P&L value:
 * <ol>
 *   <li><b>Unknown</b> — value is {@code null} / not yet computed
 *       (running cycle, pre-aggregation legacy row). Renders {@code "—"}.
 *       Never {@code "+€0.00"}.</li>
 *   <li><b>Partial</b> — value is a sum-of-known with at least one
 *       position's P&L missing. Renders the number followed by a
 *       {@code "*"} marker and a tooltip explaining the gap.</li>
 *   <li><b>Synthetic</b> — value derives from a reconciliation-inferred
 *       close (e.g. {@code RECONCILED_EXTERNALLY_CLOSED}, broker
 *       divergence backfill). Renders the number followed by a
 *       {@code "~"} marker and a tooltip flagging the inference.</li>
 * </ol>
 *
 * <p>Sign coloring follows SPEC §14: green for profit, red for loss,
 * muted for flat/zero or unknown. Figures use the tabular monospace face
 * (anti layout-shift on real-time refresh).
 */
export interface PnlValueProps {
  /** EUR amount. Null/undefined/NaN all render as unknown. */
  value: number | null | undefined;
  /**
   * False ⇒ render as partial with the {@code *} marker. True or null
   * (legitimately complete or pre-aggregation legacy) ⇒ no partial
   * marker. Ignored when {@code value} is null/undefined.
   */
  pnlComplete?: boolean | null;
  /**
   * True ⇒ render the {@code ~} marker indicating a synthetic /
   * reconciliation-derived close price. Defaults to false. Ignored when
   * {@code value} is null/undefined.
   */
  synthetic?: boolean;
  /** Optional currency symbol. Defaults to "€". */
  currency?: string;
  /** Forces a sign prefix for non-negative values. Defaults to true. */
  withSign?: boolean;
  /** Optional extra className for callers that wrap with a layout. */
  className?: string;
  /** data-testid passthrough. */
  testId?: string;
}

const UNKNOWN = "—";
const BASE = "pnl-value font-mono tabular-nums tracking-tight";

export default function PnlValue({
  value,
  pnlComplete,
  synthetic = false,
  currency = "€",
  withSign = true,
  className,
  testId,
}: PnlValueProps) {
  const n = toNumber(value);
  if (n == null) {
    return (
      <span
        className={cn(BASE, "pnl-value--unknown text-muted-foreground", className)}
        title="Valeur pas encore connue. Cycles en cours et positions sans prix de clôture réglé rendent —, jamais +€0.00."
        data-testid={testId}
      >
        {UNKNOWN}
      </span>
    );
  }

  const sign = n > 0 ? "profit" : n < 0 ? "loss" : "flat";
  const isPartial = pnlComplete === false;
  const marker = synthetic ? "~" : isPartial ? "*" : "";
  const title = synthetic
    ? "Clôture synthétique : P&L dérivé d'un prix inféré par réconciliation, pas un fill IG confirmé."
    : isPartial
      ? "Partiel : au moins une position fermée n'avait pas de quoi calculer son P&L — le total ne somme que les P&L connus."
      : undefined;

  const text = `${withSign && n >= 0 ? "+" : ""}${currency}${Math.abs(n).toFixed(2)}`;
  const signedText = n < 0 ? `-${currency}${Math.abs(n).toFixed(2)}` : text;

  return (
    <span
      className={cn(
        BASE,
        `pnl-value--${sign}`,
        sign === "profit" && "text-profit",
        sign === "loss" && "text-loss",
        sign === "flat" && "text-muted-foreground",
        isPartial && "pnl-value--partial",
        synthetic && "pnl-value--synthetic",
        className,
      )}
      title={title}
      data-testid={testId}
    >
      {signedText}
      {marker && (
        <span
          className="pnl-value__marker align-super text-[0.7em] opacity-80"
          aria-label={synthetic ? "P&L synthétique" : "P&L partiel"}
        >
          {marker}
        </span>
      )}
    </span>
  );
}

function toNumber(v: number | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
